import type React from "react";
import {
  Image,
  type ImageSourcePropType,
  type ImageStyle,
  type StyleProp,
  Text,
  View,
} from "react-native";
import { CARD_RADIUS, CardSurface, Row } from "@louise/moon-ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { useTaskSectionState } from "../hooks/useTaskSectionState";

type TimelineSubtaskCardProps = {
  id: string;
  title: string;
  subtitle?: string;
  titleIcon?: React.ReactNode;
  titleImage?: ImageSourcePropType;
  titleImageStyle?: StyleProp<ImageStyle>;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  collapsedPreviewHeight?: number;
  children: React.ReactNode;
};

export function TimelineSubtaskCard({
  id,
  title,
  subtitle,
  titleIcon,
  titleImage,
  titleImageStyle,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onToggle: controlledOnToggle,
  children,
}: TimelineSubtaskCardProps) {
  const t = useTranslations();
  const taskSectionState = useTaskSectionState(id, {
    defaultExpanded,
  });
  const expanded = controlledExpanded ?? taskSectionState.expanded;
  const toggleExpanded = controlledOnToggle ?? taskSectionState.toggleExpanded;

  return (
    <CardSurface style={{ padding: 16, overflow: "hidden" }}>
      <Accordion
        type="multiple"
        value={expanded ? [id] : []}
        onValueChange={(next: string[]) => {
          if (next.includes(id) !== expanded) toggleExpanded();
        }}
      >
        <AccordionItem value={id}>
          <AccordionTrigger
            accessibilityLabel={
              expanded
                ? t("homepage.task_section_card.collapse_title", {
                    params: { title },
                  })
                : t("homepage.task_section_card.expand_title", {
                    params: { title },
                  })
            }
            analyticsLabel={title}
            action={id}
            className="items-center gap-3 py-0"
          >
            <View
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
              }}
            >
              {titleIcon ? (
                <View
                  style={{
                    width: 36,
                    height: 42,
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {titleIcon}
                </View>
              ) : titleImage ? (
                <Image
                  source={titleImage}
                  resizeMode="contain"
                  style={[
                    { width: 24, height: 42, flexShrink: 0 },
                    titleImageStyle,
                  ]}
                />
              ) : null}
              <View style={{ flex: 1 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 17,
                    lineHeight: 22,
                    color: EMBER.colors.text,
                    fontWeight: "700",
                  }}
                >
                  {title}
                </Text>
                {subtitle ? (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      lineHeight: 18,
                      color: EMBER.colors.textLight,
                      fontWeight: "400",
                      marginTop: 3,
                    }}
                  >
                    {subtitle}
                  </Text>
                ) : null}
              </View>
            </View>
          </AccordionTrigger>

          <AccordionContent className="pb-0">
            <View style={{ paddingTop: 14 }}>{children}</View>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </CardSurface>
  );
}

export function TimelineActionRow({
  title,
  subtitle,
  icon,
  onPress,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onPress: () => void;
}) {
  return (
    <Row
      title={title}
      subtitle={subtitle}
      leading={icon}
      leadingSize={icon ? 36 : 0}
      gap={10}
      paddingHorizontal={16}
      paddingVertical={12}
      borderRadius={CARD_RADIUS}
      onPress={onPress}
      accessibilityLabel={title}
    />
  );
}
